// src/types/settings.ts

// Directional values with units (for padding and margin)
export interface DirectionalValue {
  top: number;
  right: number;
  bottom: number;
  left: number;
  unit: 'px' | '%' | 'em' | 'rem' | 'vh' | 'vw';
  linked: boolean;
}

// Heading settings for letter case
export interface HeadingSettings {
  h1: boolean;
  h2: boolean;
  h3: boolean;
  h4: boolean;
  h5: boolean;
  h6: boolean;
  [key: string]: boolean;
}

// Letter case conditionals
export interface LetterCaseConditionals {
  enabled: boolean;
  headings: {
    h1?: 'title-case' | 'sentence-case' | 'lowercase' | 'uppercase' | null;
    h2?: 'title-case' | 'sentence-case' | 'lowercase' | 'uppercase' | null;
    h3?: 'title-case' | 'sentence-case' | 'lowercase' | 'uppercase' | null;
    h4?: 'title-case' | 'sentence-case' | 'lowercase' | 'uppercase' | null;
    h5?: 'title-case' | 'sentence-case' | 'lowercase' | 'uppercase' | null;
    h6?: 'title-case' | 'sentence-case' | 'lowercase' | 'uppercase' | null;
    [key: string]: 'title-case' | 'sentence-case' | 'lowercase' | 'uppercase' | null | undefined;
  }
}

// Letter case tool settings
export interface LetterCaseSettings {
  enabled: 'yes' | 'no';
  headings: HeadingSettings;
  type: 'title-case' | 'sentence-case' | 'lowercase' | 'uppercase';
  conditionals: LetterCaseConditionals;
}

// Position options
export type ButtonPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

// Display options
export type DisplayOn = 'all' | 'posts' | 'pages';

// Animation types
export type AnimationType = 'none' | 'fade' | 'slide' | 'bounce';

// Scroll direction
export type ScrollDirection = 'down' | 'up' | 'both';

// Tools settings
export interface ToolsSettings {
  enabled: 'yes' | 'no';
  use_react_ui: 'yes' | 'no';
  category_id?: number;
  understandability?: '' | 'low' | 'medium' | 'high';
  credibility?: '' | 'low' | 'medium' | 'high';
  deliverability?: '' | 'low' | 'medium' | 'high';
}

// Main plugin settings
export interface KalicubeToolboxSettings {
  button_enabled: 'yes' | 'no'; 
  button_text: string;
  button_url: string;
  button_icon: string;
  button_color: string;
  text_color: string;
  button_position: ButtonPosition;
  button_padding: DirectionalValue;
  font_size: number;
  border_radius: number;
  button_margin: DirectionalValue;
  box_shadow: 'yes' | 'no';
  display_on: DisplayOn;
  show_on_mobile: 'yes' | 'no';
  animation_type: AnimationType;
  z_index: number;
  scroll_animation_enabled: 'yes' | 'no';
  scroll_threshold: number;
  scroll_direction: ScrollDirection;
  letter_case: LetterCaseSettings;
  clerkPublishableKey?: string;
  
  // Link management
  link_management_enabled: 'yes' | 'no';
  
  // Tools settings
  tools: ToolsSettings;
}

// Default settings
export const defaultSettings: KalicubeToolboxSettings = {
  button_enabled: 'no',
  button_text: 'Contact Us',
  button_url: '/contact',
  button_icon: 'fas fa-phone',
  button_color: '#0073aa',
  text_color: '#ffffff',
  button_position: 'bottom-right',
  button_padding: {
    top: 12,
    right: 12,
    bottom: 12,
    left: 12,
    unit: 'px',
    linked: true
  },
  font_size: 16,
  border_radius: 50,
  button_margin: {
    top: 20,
    right: 20,
    bottom: 20,
    left: 20,
    unit: 'px',
    linked: true
  },
  box_shadow: 'yes',
  display_on: 'all',
  show_on_mobile: 'yes',
  animation_type: 'fade',
  z_index: 9999,
  scroll_animation_enabled: 'no',
  scroll_threshold: 20,
  scroll_direction: 'down',
  letter_case: {
    enabled: 'no',
    headings: {
      h1: true,
      h2: true,
      h3: true,
      h4: false,
      h5: false,
      h6: false
    },
    type: 'title-case',
    conditionals: {
      enabled: false,
      headings: {
        h1: null,
        h2: null,
        h3: null,
        h4: null,
        h5: null,
        h6: null
      }
    }
  },
  
  // Link management
  link_management_enabled: 'yes',
  
  // Tools settings
  tools: {
    enabled: 'yes',
    use_react_ui: 'yes',
    category_id: 1,
    understandability: '',
    credibility: '',
    deliverability: '',
  }
};